import { ArrowRight, Phone, CheckCircle, Truck } from "lucide-react";
import { HERO, COMPANY } from "../data/mock";
import { Button } from "./ui/button";

export const HeroSection = () => {
  const scrollToQuote = () => {
    const el = document.querySelector("#quote");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section className="relative overflow-hidden min-h-[92vh] flex items-center" style={{ background: 'linear-gradient(135deg, #070e24 0%, #0c1839 25%, #11214d 50%, #0c1839 75%, #070e24 100%)' }}>
      {/* Background Image with better visibility */}
      <div className="absolute inset-0">
        <img
          src={HERO.bgImage}
          alt="Trucking fleet"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(135deg, rgba(7,14,36,0.92) 0%, rgba(12,24,57,0.85) 40%, rgba(17,33,77,0.80) 60%, rgba(7,14,36,0.95) 100%)' }} />
      </div>

      {/* Decorative geometric elements */}
      <div className="absolute top-0 right-0 w-[500px] h-[500px] opacity-30 hidden md:block">
        <div className="absolute top-20 right-20 w-72 h-72 rounded-full" style={{ background: 'radial-gradient(circle, rgba(30,64,175,0.25) 0%, transparent 70%)' }} />
        <div className="absolute top-40 right-40 w-48 h-48 rounded-full" style={{ background: 'radial-gradient(circle, rgba(220,38,38,0.15) 0%, transparent 70%)' }} />
      </div>

      <div className="absolute bottom-0 left-0 w-[400px] h-[400px] opacity-20 hidden md:block">
        <div className="absolute bottom-10 left-10 w-80 h-80 rounded-full" style={{ background: 'radial-gradient(circle, rgba(30,64,175,0.2) 0%, transparent 70%)' }} />
      </div>

      {/* Decorative grid lines */}
      <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />

      {/* Accent line */}
      <div className="absolute top-0 left-0 right-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent, #1E40AF, #DC2626, transparent)' }} />

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="max-w-3xl">
          {/* Label Badge */}
          <div className="inline-flex items-center gap-2.5 bg-white/[0.07] backdrop-blur-xl border border-white/[0.08] rounded-full px-5 py-2.5 mb-10 shadow-lg shadow-black/10">
            <Truck size={14} className="text-blue-400" />
            <span className="text-blue-200/80 text-sm font-medium tracking-wide">{HERO.label}</span>
          </div>

          {/* Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-[3.5rem] xl:text-[4.25rem] font-extrabold text-white leading-[1.08] tracking-tight mb-7">
            STOP LOSING MONEY ON{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #EF4444, #DC2626)' }}>LATE FREIGHT</span>
            </span>{" "}
            &{" "}
            <span className="relative inline-block">
              <span className="relative z-10 text-transparent bg-clip-text" style={{ backgroundImage: 'linear-gradient(135deg, #EF4444, #DC2626)' }}>BROKEN PROMISES</span>
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-slate-300/80 leading-relaxed mb-4 max-w-2xl">
            {HERO.subheadline}
          </p>
          <p className="text-lg sm:text-xl text-white font-bold mb-9 tracking-wide">
            {HERO.highlight}
          </p>

          {/* Risk Reversal */}
          <div className="flex items-center gap-2.5 mb-11 bg-emerald-500/[0.08] border border-emerald-500/15 rounded-xl px-5 py-3 w-fit">
            <CheckCircle size={18} className="text-emerald-400 shrink-0" />
            <span className="text-emerald-200/90 font-medium text-sm">{HERO.riskReversal}</span>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <Button
              onClick={scrollToQuote}
              className="bg-hevi-red hover:bg-red-700 text-white font-bold text-base px-9 h-14 rounded-xl shadow-xl shadow-red-900/30 hover:shadow-red-900/50 transition-all duration-300 group"
            >
              {HERO.ctaPrimary}
              <ArrowRight size={18} className="ml-2 group-hover:translate-x-1 transition-transform duration-200" />
            </Button>
            <a href={`tel:${COMPANY.phone}`}>
              <Button
                variant="outline"
                className="border border-white/15 text-white hover:bg-white/[0.08] font-semibold text-base px-9 h-14 rounded-xl transition-all duration-300 w-full sm:w-auto bg-white/[0.04] backdrop-blur-sm"
              >
                <Phone size={18} className="mr-2 text-blue-400" />
                {HERO.ctaSecondary}
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* Bottom gradient transition */}
      <div className="absolute bottom-0 left-0 right-0 h-32" style={{ background: 'linear-gradient(to top, hsl(220, 25%, 97%), transparent)' }} />
    </section>
  );
};
